import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { CheckCircle2, XCircle, Shield, FileCheck2, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import ExportMenu from "../components/ExportMenu";
import WorkingPapersPanel from "../components/WorkingPapersPanel";

const AREA_ICON = { "Accounting": FileCheck2, "Balance Sheet": FileCheck2, "P&L": FileCheck2, "Banking": FileCheck2, "Fraud": AlertTriangle, "Compliance": Shield, "Controls": Shield };

export default function Audit() {
    const [data, setData] = useState(null);
    const [areaFilter, setAreaFilter] = useState("All");

    useEffect(() => { api.get("/audit/checks").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING AUDIT CHECKS...</div>;

    const areas = ["All", ...new Set(data.checks.map((c) => c.area))];
    const filtered = data.checks.filter((c) => areaFilter === "All" || c.area === areaFilter);
    const failed = filtered.filter((c) => c.status === "FAIL");
    const passed = filtered.filter((c) => c.status === "PASS");

    const pieData = [
        { name: "Passed", value: data.summary.passed, fill: "#4af6c3" },
        { name: "Failed", value: data.summary.failed, fill: "#ff433d" },
    ];

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// AUDIT & ASSURANCE</div>
                    <h1 className="font-display font-black text-3xl flex items-center gap-3"><Shield className="text-[var(--brand-amber)]" size={26} /> Data You Can Sign On</h1>
                    <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                        17 automated checks · Assertions: Existence · Completeness · Accuracy · Valuation · Cut-off · Compliance
                    </div>
                </div>
                <ExportMenu kind="audit" label="Audit Summary" testId="export-audit" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="au-total" label="Total Checks" value={data.summary.total} format="custom" accent />
                <KpiCard testId="au-pass" label="Passed" value={data.summary.passed} format="custom" sub={`${data.summary.coverage_pct}% coverage`} />
                <KpiCard testId="au-fail" label="Flags / Amber" value={data.summary.failed} format="custom" sub="Explain / action" />
                <KpiCard testId="au-cov" label="Coverage" value={`${data.summary.coverage_pct}%`} format="custom" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="au-donut" title="Assurance Coverage">
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={(e) => `${e.name}: ${e.value}`}>
                                    {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel testId="au-legend" className="xl:col-span-2" title="What This Means" subtitle="For CFO & Statutory Auditors">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-body">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-pos mb-2 flex items-center gap-1"><CheckCircle2 size={12} /> PASS</div>
                            <p className="text-[var(--text-secondary)] leading-relaxed">The figure has been independently recomputed / vouched / reconciled and matches within tolerance. Auditor can rely on this for management assertion.</p>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-neg mb-2 flex items-center gap-1"><AlertTriangle size={12} /> FLAG (Amber)</div>
                            <p className="text-[var(--text-secondary)] leading-relaxed">Genuine operational issue detected (fraud alert, unfiled statutory return, in-transit reconciliation item). Requires management action, not a book-keeping error.</p>
                        </div>
                        <div className="md:col-span-2 border-t border-panel pt-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] mb-2">Assurance Standards Applied</div>
                            <div className="text-[var(--text-secondary)] leading-relaxed">
                                Aligned to <span className="text-[var(--text-primary)]">SA 315 (Risk Assessment)</span>, <span className="text-[var(--text-primary)]">SA 330 (Response to Risks)</span>, <span className="text-[var(--text-primary)]">SA 500 (Audit Evidence)</span>, and <span className="text-[var(--text-primary)]">CARO 2020</span> disclosure norms. Every underlying journal traces to source document → account ledger → trial balance → financial statement — full audit trail.
                            </div>
                        </div>
                    </div>
                </Panel>
            </div>

            <div className="flex items-center gap-2 font-mono-fin text-[10px] uppercase tracking-wider flex-wrap">
                <span className="text-[var(--text-secondary)]">Area:</span>
                {areas.map((a) => (
                    <button
                        key={a}
                        data-testid={`au-area-${a.replace(/\s|&/g, "").toLowerCase()}`}
                        onClick={() => setAreaFilter(a)}
                        className={`px-3 py-1 border ${areaFilter === a ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)] text-[var(--text-primary)]" : "border-panel text-[var(--text-secondary)]"}`}
                    >
                        {a}
                    </button>
                ))}
            </div>

            {failed.length > 0 && (
                <Panel testId="au-fails" title="Flags Requiring Attention" subtitle={`${failed.length} of ${filtered.length}`}>
                    <div className="space-y-2">
                        {failed.map((c, i) => (
                            <div key={i} className="border border-neg/40 bg-[rgba(255,67,61,0.05)] p-3 flex items-start gap-3">
                                <XCircle size={16} className="text-neg mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[9px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 border border-panel text-[var(--text-secondary)]">{c.area}</span>
                                        <span className="text-xs text-[var(--text-primary)] font-body font-medium">{c.name}</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-[10px] font-mono-fin uppercase tracking-wider">
                                        <div><span className="text-[var(--text-secondary)]">Expected:</span> <span className="text-[var(--text-primary)]">{c.expected}</span></div>
                                        <div><span className="text-[var(--text-secondary)]">Actual:</span> <span className="text-amber">{c.actual}</span></div>
                                    </div>
                                    <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-1 italic">{c.comment}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            <Panel testId="au-passes" title="Passed Checks" subtitle={`${passed.length} of ${filtered.length}`}>
                <div className="space-y-1">
                    {passed.map((c, i) => (
                        <div key={i} className="border border-panel p-2.5 flex items-start gap-3 hover:bg-[var(--bg-panel-hover)]">
                            <CheckCircle2 size={14} className="text-pos mt-0.5 shrink-0" />
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                                <div className="md:col-span-2">
                                    <div className="text-xs text-[var(--text-primary)]">{c.name}</div>
                                    <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">{c.area} · {c.comment}</div>
                                </div>
                                <div className="text-[10px] font-mono-fin"><span className="text-[var(--text-secondary)]">Expected:</span> <span className="text-[var(--text-primary)]">{c.expected}</span></div>
                                <div className="text-[10px] font-mono-fin"><span className="text-[var(--text-secondary)]">Actual:</span> <span className="text-pos">{c.actual}</span></div>
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>

            <WorkingPapersPanel />
        </div>
    );
}
